import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { LogOut, Package, ExternalLink, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const tagCls = (tone) =>
  ({
    paid: "bg-[#4A5D23] text-[#FFFDF5]",
    pending: "bg-[#FDE047]",
    failed: "bg-[#D95D39] text-[#FFFDF5]",
    unfulfilled: "bg-[#1C1914]/10",
    queued: "bg-[#FDE047]",
    submitted: "bg-[#FDE047]",
    fulfilled: "bg-[#4A5D23] text-[#FFFDF5]",
    manual_required: "bg-[#D95D39] text-[#FFFDF5]",
  })[tone] || "bg-[#1C1914]/10";

const AccountPage = () => {
  const { user, loading, isAuthenticated, logout, authHeader } = useAuth();
  const nav = useNavigate();

  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [ordersError, setOrdersError] = useState("");

  // Redirect to login if unauthenticated (after the initial verify completes)
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      nav("/login?next=/account", { replace: true });
    }
  }, [loading, isAuthenticated, nav]);

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    const fetchOrders = async () => {
      setOrdersLoading(true);
      try {
        const res = await axios.get(`${API}/auth/orders`, { headers: authHeader });
        if (!cancelled) setOrders(res.data || []);
      } catch (e) {
        console.warn("[account] orders fetch failed:", e);
        if (!cancelled) setOrdersError("Couldn't load your orders. Try refreshing.");
      } finally {
        if (!cancelled) setOrdersLoading(false);
      }
    };
    fetchOrders();
    return () => { cancelled = true; };
  }, [isAuthenticated, authHeader]);

  if (loading || !isAuthenticated) {
    return (
      <div className="px-4 md:px-8 py-20 min-h-[70vh] flex items-center justify-center" data-testid="account-loading">
        <Loader2 size={32} className="animate-spin" />
      </div>
    );
  }

  return (
    <div className="px-4 md:px-8 py-12 md:py-16 min-h-[70vh]" data-testid="account-page">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
          <div>
            <p className="font-condensed text-sm uppercase tracking-widest text-[#D95D39]">// my account</p>
            <h1 className="font-display text-4xl md:text-5xl uppercase leading-none mt-1" data-testid="account-greeting">
              {user.name ? `Hi, ${user.name}.` : "Hi."}
            </h1>
            <p className="font-body text-sm text-[#1C1914]/60 mt-2">{user.email}</p>
          </div>
          <button
            onClick={logout}
            className="border-2 border-[#1C1914] px-3 py-2 font-condensed text-sm uppercase tracking-widest hover:bg-[#FDE047] flex items-center gap-2"
            data-testid="account-logout"
          >
            <LogOut size={14} /> Log out
          </button>
        </div>

        {/* Orders */}
        <div className="mb-4 flex items-center gap-2">
          <Package size={20} />
          <h2 className="font-display text-2xl uppercase">Your orders</h2>
          <span className="font-condensed text-xs uppercase tracking-widest text-[#1C1914]/60 ml-2">
            ({orders.length})
          </span>
        </div>

        {ordersLoading && (
          <div className="border-4 border-[#1C1914] bg-[#FFFDF5] p-6 text-center" data-testid="account-orders-loading">
            <Loader2 size={20} className="mx-auto animate-spin" />
          </div>
        )}

        {!ordersLoading && ordersError && (
          <div className="border-4 border-[#D95D39] bg-[#D95D39]/10 p-4 font-body text-sm text-[#D95D39]" data-testid="account-orders-error">
            {ordersError}
          </div>
        )}

        {!ordersLoading && !ordersError && orders.length === 0 && (
          <div className="border-4 border-[#1C1914] bg-[#FFFDF5] p-8 text-center" data-testid="account-orders-empty">
            <p className="font-display text-2xl uppercase">Nothing here yet.</p>
            <p className="font-body text-sm text-[#1C1914]/70 mt-2">
              You haven't bought anything dumb yet. Let's fix that.
            </p>
            <Link to="/" className="btn-duds inline-block mt-5" data-testid="account-shop-link">
              Browse the shop
            </Link>
          </div>
        )}

        {!ordersLoading && orders.length > 0 && (
          <div className="space-y-3" data-testid="account-orders-list">
            {orders.map((o) => (
              <div
                key={o.id || o.session_id}
                className="border-4 border-[#1C1914] bg-[#FFFDF5] p-4 md:p-5"
                data-testid={`account-order-${o.session_id}`}
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="font-condensed text-xs uppercase tracking-widest text-[#1C1914]/60">
                      {o.created_at ? new Date(o.created_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "—"}
                    </p>
                    <p className="font-display text-2xl uppercase mt-1">
                      A${(o.total_usd || 0).toFixed(2)}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span className={`inline-block px-2 py-1 font-condensed text-xs uppercase tracking-widest ${tagCls(o.payment_status)}`}>
                      {o.payment_status || "pending"}
                    </span>
                    <span className={`inline-block px-2 py-1 font-condensed text-xs uppercase tracking-widest ${tagCls(o.fulfillment_status)}`}>
                      {o.fulfillment_status || "—"}
                    </span>
                  </div>
                </div>

                {(o.lines || []).length > 0 && (
                  <div className="mt-3 pt-3 border-t-2 border-dashed border-[#1C1914]/30 space-y-1">
                    {(o.lines || []).map((l, i) => (
                      <div key={`${l.product_id}-${l.size}-${l.color || ""}-${i}`} className="flex items-center justify-between font-body text-sm">
                        <span>
                          {l.quantity}× {l.product_name}{l.color ? ` — ${l.color}` : ""} <span className="text-[#1C1914]/60">({l.size})</span>
                        </span>
                        <span className="font-display">A${((l.unit_price_usd || 0) * (l.quantity || 1)).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                )}

                {o.tracking_url && (
                  <a
                    href={o.tracking_url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 mt-3 font-condensed text-sm uppercase tracking-widest text-[#D95D39] hover:underline"
                    data-testid={`account-track-${o.session_id}`}
                  >
                    Track shipment <ExternalLink size={12} />
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AccountPage;
